import React, { type FC, useEffect } from 'react'
import {
  Outlet,
  useNavigate,
  useLocation,
  ScrollRestoration,
} from 'react-router'
import { styled } from '@linaria/react'
import { Toast } from '../ui/Toast'
import { useTelegram } from '../hooks/useTelegram'
import { useViewport } from '../hooks/useViewport'
import { useTelegramInit } from '../hooks/useTelegramInit'
import { useOnboardingContext } from '../contexts/OnboardingContext'
import { useTonProofSetup } from '../hooks/useTonProofSetup'
import { useWalletConnection } from '../hooks/useWalletConnection'
import { parseStartParam, parseUrlParams } from '../functions/startParam'
import { isInTelegram } from '../hooks/useTelegram'
import {
  indexPagePath,
  indexMyPagePath,
  indexMyVotesPagePath,
  indexFinishedPagePath,
} from '../pages/IndexPage'
import { createMoneyPoolPagePath } from '../pages/CreateMoneyPoolPage'
import { onboardingPagePath } from '../pages/OnboardingPage'
import { singleBoxPageBoxPath } from '../pages/SingleBoxPage'

export const RootLayout: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  useTelegramInit()
  const { tg, isTelegramVersionSupported } = useTelegram()
  const { safeAreaTop } = useViewport()

  // initial settings for TMA
  useEffect(() => {
    // BackButton is only available in Telegram WebApp version 6.1+
    if (!isTelegramVersionSupported(6.1)) {
      return undefined
    }

    const indexPaths: string[] = [
      indexPagePath,
      indexMyPagePath,
      indexMyVotesPagePath,
      indexFinishedPagePath,
    ]

    if (indexPaths.includes(location.pathname)) {
      tg.BackButton.hide()
      return undefined
    }

    const handleBack = () => {
      navigate(-1)
    }

    tg.BackButton.show()
    tg.BackButton.onClick(handleBack)

    return () => {
      tg.BackButton.offClick(handleBack)
    }
  }, [tg, navigate, location.pathname, isTelegramVersionSupported])

  const { isCompleted: isOnboardingCompleted } = useOnboardingContext()
  useTonProofSetup() // Setup TON Connect proof before wallet connection
  useWalletConnection()

  // перенаправление на страницу с боксом
  useEffect(() => {
    let box: string | null = null

    // Try Telegram start_param first
    if (isInTelegram()) {
      const startParam = tg.initDataUnsafe.start_param
      const parsed = parseStartParam(startParam)
      box = parsed.box
    } else {
      // Fallback to browser URL params
      const urlParams = parseUrlParams()
      box = urlParams.box
    }

    if (box) {
      navigate(`${singleBoxPageBoxPath}${box}`, { replace: true })
    }
    // Должно сработать 1 раз
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Если onboarding пройден, переходим на главную
  useEffect(() => {
    if (
      isOnboardingCompleted === true &&
      location.pathname === onboardingPagePath
    ) {
      navigate(indexPagePath)
    }
  }, [isOnboardingCompleted, navigate, location.pathname])

  return (
    <>
      <Toast />
      <FullSizeAppContainer $paddingTop={safeAreaTop}>
        <Outlet />
        <ScrollRestoration
          getKey={location => {
            const indexPaths: string[] = [
              indexPagePath,
              indexMyPagePath,
              indexMyVotesPagePath,
              indexFinishedPagePath,
            ]

            // Index pages: restore by pathname
            if (indexPaths.includes(location.pathname)) {
              return location.pathname
            }

            // Create page: always reset (force unique key)
            if (location.pathname === createMoneyPoolPagePath) {
              return `${location.pathname}-${Date.now()}`
            }

            // Other routes: default browser behavior
            return location.key
          }}
        />
      </FullSizeAppContainer>
    </>
  )
}

const FullSizeAppContainer = styled.div<{ $paddingTop: number }>`
  /* Disable text selection only on touch devices (no hover capability) */
  @media (hover: none) {
    user-select: none;
    -webkit-user-select: none;
  }
  padding-top: ${props => `${props.$paddingTop}px`};
  min-width: 320px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
`
